import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Phone, MessageCircle, ChevronDown, Headset, LifeBuoy, ChevronRight, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { agentService } from '../../services/agent/agentService';
import toast from 'react-hot-toast';

const AccountManager = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRequestCallback = async () => {
    if (isRequesting) return;
    setIsRequesting(true);
    
    // Add toast loading immediately
    const toastId = toast.loading('Sending callback request...');
    
    try {
      const response = await agentService.requestCallback();
      
      if (response?.success) {
        toast.success('Callback request sent successfully!', { id: toastId });
      } else {
        throw new Error(response?.message || 'Failed to send request');
      }
    } catch (error) {
      console.error('Error requesting callback:', error);
      toast.error('Failed to send callback request. Please try again.', { id: toastId });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleNavigateToSupport = () => {
    setIsOpen(false);
    navigate('/support?tab=helpdesk');
  };

  return (
    <div 
      className="relative" 
      ref={dropdownRef}
    >
      {/* Contact Pill Button */}
      <div 
        data-tour="rm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 sm:gap-3 bg-white/10 hover:bg-white/20 p-1.5 sm:pl-3 sm:pr-4 sm:py-1.5 rounded-xl sm:rounded-2xl cursor-pointer transition-all duration-300 group shadow-sm active:scale-95 select-none shrink-0"
        title="Click for Contact & Support"
      >
        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-white/15 flex items-center justify-center overflow-hidden shadow-inner transition-all shrink-0">
          <Headset className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        
        <div className="hidden sm:flex flex-col text-left">
          <span className="text-white/90 group-hover:text-white transition-colors text-xs font-semibold leading-tight truncate max-w-[140px]">Support</span>
          <div className="flex items-center gap-1 text-[9px] text-teal-100/70 font-semibold uppercase tracking-wider leading-tight mt-0.5 group-hover:text-white transition-colors">
            <span>Contact Us</span>
            <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
          </div>
        </div>
      </div>

      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 sm:hidden z-40 backdrop-blur-xs transition-opacity" 
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Popover */}
      <div 
        className={`fixed inset-x-3.5 top-16 max-h-[calc(100vh-80px)] overflow-y-auto sm:overflow-visible sm:max-h-none sm:inset-x-auto sm:top-[calc(100%+14px)] sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:absolute w-auto sm:w-[380px] max-w-sm sm:max-w-none mx-auto sm:mx-0 bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] border border-slate-100/80 z-50 transition-all duration-300 ease-out ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-3 pointer-events-none'
        }`}
      >
        {/* Arrow centered under the Support button */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white transform rotate-45 border-l border-t border-slate-100/80 hidden sm:block"></div>
        
        <div className="relative p-4 sm:p-5">
          <button 
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer p-1"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Account Manager Details */}
          <div className="flex items-center gap-3 mb-3 sm:mb-4 pt-1 pr-6">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0 shadow-sm text-[#067978] font-bold text-base">
               TC
            </div>
            <div className="min-w-0">
              <h3 className="text-slate-800 font-bold text-sm sm:text-base leading-snug">Hi, I am Tanmay Chavhan</h3>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">Your Account Manager</p>
            </div>
          </div>

          <p className="text-slate-600 text-xs sm:text-sm mb-4 sm:mb-5 leading-relaxed">
            I am here to help you get the most out of Trrip. Reach out anytime for guidance, onboarding, or platform support.
          </p>

          <div className="relative flex items-center justify-center mb-4 sm:mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative px-2 sm:px-3 bg-white text-[10px] sm:text-[11px] font-normal text-slate-400 uppercase tracking-wider text-center">
              For demos, training, upgrades, or escalations
            </div>
          </div>

          <button 
            onClick={handleRequestCallback}
            disabled={isRequesting}
            className="w-full py-2.5 rounded-xl border-2 border-[#067978] text-[#067978] font-bold hover:bg-teal-50 transition-colors mb-3.5 sm:mb-4 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isRequesting ? 'Requesting...' : 'Request Call Back'}
          </button>

          <div className="flex flex-col gap-2">
            <a href="mailto:tanmay@trrip.live" className="w-full flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-700 py-2 px-3 rounded-xl text-xs sm:text-sm transition-colors font-semibold">
              <Mail className="w-4 h-4 text-[#067978] shrink-0" />
              <span>tanmay@trrip.live</span>
            </a>
            <div className="grid grid-cols-2 gap-2">
              <a href="tel:+919225296763" className="flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-700 py-2 px-2 rounded-xl text-xs transition-colors font-semibold whitespace-nowrap">
                <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span>+91 92252 96763</span>
              </a>
              <a href="https://wa.me/919225296763" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-700 py-2 px-2 rounded-xl text-xs transition-colors font-semibold whitespace-nowrap">
                <MessageCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Underlined Text Link for Chat with Support */}
          <div className="mt-3 sm:mt-4 pt-2.5 sm:pt-3 border-t border-slate-100 flex items-center justify-center">
            <button
              type="button"
              onClick={handleNavigateToSupport}
              className="text-xs text-slate-500 hover:text-[#067978] underline underline-offset-4 transition-colors font-medium cursor-pointer"
            >
              or chat with support team
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManager;
